<template>
  <page-container
    :loading-footer-button="loadingFooterButton"
    :hide-back-button="hideBackButton"
    :title="calendarTitle"
    @primary-cta-click="goToNextPage"
    @back-cta-click="goToBackPage"
  >
    <template #content>
      <p
        v-if="selectedAppointmentTypeFeeInDollars"
        data-cy-id="payment-message-step-c"
        class="mb-0 mt-6"
      >
        {{ appointmentWithFeeMessage }}
      </p>
      <form
        class="mt-6 pb-8"
        @submit.prevent="goToNextPage"
      >
        <pp-input-select
          v-if="showInsuranceDropdown"
          v-model="formData.insuranceProviderId"
          :items="insuranceOptions"
          :disabled="loadingFooterButton"
          :error-messages="getErrorMessages('insuranceProviderId')"
          label="Insurance"
          :readonly="isReadOnlyInsuranceProviderId"
          no-data-text="No Values"
          class="mb-5"
          @blur="blurField($v.formData.insuranceProviderId, 'insurance-provider-id')"
        />
        <pp-input-text
          v-if="showInsuranceMemberIdField"
          v-model="formData.insuranceIdNumber"
          :disabled="loadingFooterButton"
          :error-messages="getErrorMessages('insuranceIdNumber')"
          label="Insurance Member ID#"
          class="mb-5"
          @blur="blurField($v.formData.insuranceIdNumber, 'insurance-id-number')"
          @focus="scrollToView($event)"
        />
        <pp-input-text
          v-if="showInsuranceGroupIdField"
          v-model="formData.insuranceGroupNumber"
          :disabled="loadingFooterButton"
          :error-messages="getErrorMessages('insuranceGroupNumber')"
          label="Insurance Group ID#"
          class="mb-5"
          @blur="blurField($v.formData.insuranceGroupNumber, 'insurance-group-number')"
          @focus="scrollToView($event)"
        />
        <pp-input-text
          v-if="showInsurancePhoneField"
          v-model="formData.insurancePhone"
          v-mask="'(###) ### ####'"
          :disabled="loadingFooterButton"
          :error-messages="getErrorMessages('insurancePhone')"
          label="Insurance Phone#"
          type="tel"
          class="mb-5"
          @blur="blurField($v.formData.insurancePhone, 'insurance-phone')"
          @focus="scrollToView($event)"
        />
        <pp-input-textarea
          v-if="showField('comment')"
          v-model="formData.comment"
          :disabled="loadingFooterButton"
          :error-messages="getErrorMessages('comment')"
          label="Additional notes for the practice"
          class="mb-5"
          :counter="commentMaxLength"
          :maxlength="commentMaxLength"
          @blur="blurField($v.formData.comment, 'comment')"
          @focus="scrollToView($event)"
        />
        <pp-input-select
          v-if="showReferral"
          v-model="formData.referralSource"
          :disabled="loadingFooterButton"
          :items="referralSourceOptions"
          label="How did you hear about us?"
          :menu-props="{ top: true, offsetY: true }"
          no-data-text="No Values"
        />
        <!-- eslint-disable vue/no-v-html -->
        <div
          v-if="footerText"
          class="mt-4 grey--text text--darken-4"
          v-html="footerText"
        />
      </form>
    </template>
  </page-container>
</template>

<script>
import Calendar from '@/entities/Calendar'

import PageContainer from '@/components/page-container/PageContainer'
import { validationMixin } from 'vuelidate'
import { VueMaskDirective } from 'v-mask'
import { FieldErrorMessagesMixin } from '@/mixins/fieldErrorMessagesMixin'
import formAnalytics from '@/mixins/formAnalytics'
import { requiredIf } from 'vuelidate/lib/validators'
import { mapGetters, mapState } from 'vuex'
import {
  REFERRER_BACK_BUTTON,
  NO_INSURANCE_OPTION_ID,
  OTHER_INSURANCE_OPTION_ID,
  APPT_FEE_PARAGRAPH
} from '@/views/constants'
import { ScreeningSettings } from '@/utils/screeningSettings'
import { datadogRum } from '@datadog/browser-rum'
import * as uiTypes from '@/store/ui/mutations-types'
import { MAX_COMMENT_LENGTH } from '@/constants'
const screeningQuestions = new ScreeningSettings()

export default {
  name: 'ViewsStepC',
  components: {
    'page-container': PageContainer
  },
  directives: { mask: VueMaskDirective },
  mixins: [validationMixin, FieldErrorMessagesMixin, formAnalytics],
  data () {
    return {
      loadingFooterButton: false,
      apptFeeParagraph: APPT_FEE_PARAGRAPH,
      referralSourceOptions: [
        {
          text: 'Insurance provider',
          value: 'referral - insurance'
        },
        {
          text: 'Online search e.g. Google, Bing',
          value: 'online - search'
        },
        {
          text: 'Online review site e.g. Yelp, Healthgrades',
          value: 'online - reviews'
        },
        {
          text: 'Referral from healthcare provider',
          value: 'referral - provider'
        },
        {
          text: 'Referral from friend/colleague',
          value: 'referral - friend/colleague'
        },
        {
          text: 'Local advertisement',
          value: 'local ad'
        },
        {
          text: 'Other',
          value: 'other'
        }
      ]
    }
  },
  computed: {
    ...mapGetters({
      formData: 'formData/formData',
      calendarInsurances: 'legacy/calendarInsurances',
      calendarFooterText: 'legacy/calendarFooterText',
      calendarTitle: 'legacy/calendarTitle',
      screeningSettings: 'legacy/screeningSettings',
      filledSteps: 'ui/filledSteps',
      patientTypeFlagName: 'formData/patientTypeFlagName',
      selectedAppointmentTypeFeeInDollars: 'formData/selectedAppointmentTypeFeeInDollars'
    }),
    ...mapState('legacy', ['insurances', 'createdAppointment', 'calendars']),
    calendar () {
      return this.calendars[this.formData.calendarIndex] || new Calendar()
    },
    allowNoInsAnswer () {
      return this.calendar.settings.allowNoInsAnswer
    },
    insuranceProviderIdSelected () {
      return !!this.formData.insuranceProviderId
    },
    otherInsAnswerSelected () {
      return OTHER_INSURANCE_OPTION_ID === this.formData.insuranceProviderId
    },
    noInsAnswerSelected () {
      return NO_INSURANCE_OPTION_ID === this.formData.insuranceProviderId
    },
    noOrOtherInsSelected () {
      return this.noInsAnswerSelected || this.otherInsAnswerSelected
    },
    insuranceOptions () {
      const options = []
      const insurances = this.calendarInsurances(this.formData.calendarIndex)
      if (!insurances) {
        return options
      }
      insurances.forEach((insurance) => {
        const detailInsuranceProviderInfo = this.insurances[insurance.id] || insurance
        const id = detailInsuranceProviderInfo?.insuranceProviderId || insurance.id
        let valid = true
        if (this.formData.doctorId) {
          valid = detailInsuranceProviderInfo.fromOneProviderMapping || detailInsuranceProviderInfo.showOnAllProviders || (detailInsuranceProviderInfo?.providerIds ?? []).includes(this.formData.doctorId)
        }
        if ([NO_INSURANCE_OPTION_ID, OTHER_INSURANCE_OPTION_ID].includes(id)) {
          valid = this.allowNoInsAnswer
        }
        valid && options.push({ text: detailInsuranceProviderInfo?.provider, value: id })
      })
      return options
    },
    footerText () {
      return this.calendarFooterText(this.formData.calendarIndex)
    },
    appointmentAlreadySubmittedId () {
      return this.createdAppointment?.appointmentId
    },
    hideBackButton () {
      return !!this.appointmentAlreadySubmittedId
    },
    showInsuranceDropdown () {
      return this.insuranceOptions.length > 0 && this.showField('insuranceProviderId')
    },
    additionalInsuranceFieldsCouldBeShowed () {
      return this.insuranceProviderIdSelected ? !this.noOrOtherInsSelected : true
    },
    showInsuranceMemberIdField () {
      return this.showInsuranceDropdown && this.showField('insuranceIdNumber') && this.additionalInsuranceFieldsCouldBeShowed
    },
    showInsuranceGroupIdField () {
      return this.showInsuranceDropdown && this.showField('insuranceGroupNumber') && this.additionalInsuranceFieldsCouldBeShowed
    },
    showInsurancePhoneField () {
      return this.showInsuranceDropdown && this.showField('insurancePhone') && this.isRequired('insurancePhone') && this.additionalInsuranceFieldsCouldBeShowed
    },
    isReadOnlyInsuranceProviderId () {
      return !!screeningQuestions.hasInsuranceSetting(this.screeningSettings(this.formData.calendarIndex))
    },
    showReferral () {
      return !!this.formData.newPatient
    },
    appointmentWithFeeMessage () {
      return `${this.apptFeeParagraph} $${this.selectedAppointmentTypeFeeInDollars}.`
    },
    commentMaxLength () {
      return MAX_COMMENT_LENGTH
    }
  },
  created () {
    if (!this.filledSteps.stepB) {
      const dataToLog = {
        formData: {
          newPatient: this.formData.newPatient,
          isTelehealth: this.formData.isTelehealth,
          reason: this.formData.reason,
          insuranceProviderId: this.formData.insuranceProviderId,
          doctorId: this.formData.doctorId,
          locationId: this.formData.locationId,
          timeBlock: this.formData.timeBlock,
          calendarIndex: this.formData.calendarIndex,
          calendarId: this.formData.calendarId,
          scheduleId: this.formData.scheduleId,
          calendarBlockId: this.formData.calendarBlockId
        },
        practice: {
          id: this.$practiceId,
          step: 'C'
        }
      }
      const redirectToStep = this.filledSteps.stepA ? 'stepB' : 'stepA'
      datadogRum.addAction('stepC opened by directUrl', {
        message: 'stepC opened, but prev step(s) was not filled',
        ...dataToLog,
        redirectedTo: redirectToStep
      })
      this.$router.replace({ name: redirectToStep, query: this.$route.query || {} })
    }
  },
  methods: {
    goToNextPage () {
      this.loadingFooterButton = true
      this.submitAnalytics('step-c-continue')
      if (this.$v.$invalid) {
        this.$v.$touch()
        this.loadingFooterButton = false
        return
      }
      this.$store.commit(`ui/${uiTypes.SET_STEP_FILLED}`, 'stepC')
      if (this.formData.referralSource) {
        this.trackEvent({
          action: 'referral_source',
          label: this.formData.referralSource
        })
      }
      if (this.noInsAnswerSelected) {
        this.formData.insurancePhone = undefined
        this.formData.insuranceGroupNumber = undefined
        this.formData.insuranceIdNumber = undefined
      }
      const existingApptId = this.appointmentAlreadySubmittedId
      this.$store.dispatch('formData/setFormData', {
        ...this.formData,
        origin: this.$widgetOrigin
      }).then(() => {
        this.$store.dispatch('legacy/saveAppointment', this.formData)
          .then(() => {
            if (!existingApptId) {
              this.trackEvent({ action: `book_${this.patientTypeFlagName}_patient` })
              this.trackEvent({ action: 'book_complete' })
              datadogRum.addAction('confirmed', {
                practice: {
                  id: this.$practiceId,
                  step: 'C'
                }
              })
              // logging for appointments from calendar with otherProvider availability
              if (this.formData.doctorId !== this.formData.showedDoctorId && this.calendar.settings.communication.showAsOtherCalendar) {
                datadogRum.addAction('otherProvider calendar appointment', {
                  practice: {
                    id: this.$practiceId,
                    step: 'B',
                    doctorId: this.formData.doctorId,
                    showedDoctorId: this.formData.showedDoctorId
                  }
                })
              }
            } else {
              // Adding temporary a new event to investigate the data reconcile issue
              this.trackEvent({ action: 'book_complete_duplicate' })
              datadogRum.addAction('duplicated_confirmed', {
                practice: {
                  id: this.$practiceId
                }
              })
            }
            if (this.selectedAppointmentTypeFeeInDollars) {
              this.$router.replace({ name: 'payment', query: this.$route.query || {} })
            } else if (this.createdAppointment.registrationLink && this.createdAppointment.redirectToRegistration) {
              window.parent.location.replace(this.createdAppointment.registrationLink)
            } else {
              this.$router.replace({ name: 'confirmation', query: this.$route.query || {} })
            }
          })
          .catch((e) => {
            this.trackEvent({
              action: 'book_error_step_c',
              label: e.message
            })
            e.name !== 'SaveAppointmentError' && console.error(e)
            this.$router.replace({ name: 'stepA', params: { error: e }, query: this.$route.query || {} })
          })
      })
    },
    goToBackPage () {
      this.$router.replace({ name: 'stepB', params: { referrer: REFERRER_BACK_BUTTON }, query: this.$route.query || {} })
    }
  },
  validations () {
    return {
      formData: {
        insuranceProviderId: {
          required: requiredIf(() => this.isRequired('insuranceProviderId'))
        },
        insuranceIdNumber: {
          required: requiredIf(() => !this.noOrOtherInsSelected && this.isRequired('insuranceIdNumber'))
        },
        insuranceGroupNumber: {
          required: requiredIf(() => !this.noOrOtherInsSelected && this.isRequired('insuranceGroupNumber'))
        },
        insurancePhone: {
          required: requiredIf(() => (!this.noOrOtherInsSelected && this.showInsurancePhoneField)),
          validPhone: (v) => this.validPhone(v, !this.showInsurancePhoneField)
        },
        comment: {
          required: requiredIf(() => this.isRequired('comment'))
        }
        // If more fields are added, update FieldErrorMessagesMixin.reRequiredFieldsOnStepC()
      }
    }
  }
}
</script>
